import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  Clock3,
  CreditCard,
  FileText,
  HeartHandshake,
  MapPin,
  Phone,
  Plane,
  ShieldCheck,
  Sparkles,
  UsersRound,
  WalletCards,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  CZECH_WORK_CONTACT,
  CZECH_WORK_HERO,
  CZECH_WORK_OPPORTUNITY,
} from "@/lib/czechWorkOpportunity";

const benefitIcons = [BriefcaseBusiness, Plane, HeartHandshake, UsersRound, ArrowRight, ShieldCheck];

export default function CzechWorkOpportunityPage() {
  useEffect(() => {
    document.title = "Чех Улсад ажиллах 10 нээлттэй байр | United Dream Mongolia";
    const description = document.querySelector('meta[name="description"]');
    description?.setAttribute(
      "content",
      "Чех Улсад ажиллах 10 нээлттэй байрны бүртгэл. 45 хүртэлх насны хүмүүст, 10 сарын 5 хүртэл бүртгүүлнэ."
    );
  }, []);

  return (
    <div className="min-h-screen bg-[#f7fafc] text-[#0b1f33]">
      <Header />

      <main>
        <section className="relative isolate overflow-hidden bg-[#081b33]">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${CZECH_WORK_HERO})` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#061629] via-[#0c2b47]/88 to-[#0d3d61]/38" />
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.14) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.14) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
          <div className="absolute -right-20 top-20 h-80 w-80 rounded-full bg-red-600/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#f7fafc]" />

          <div className="container relative z-10 pb-28 pt-8 sm:pb-32 sm:pt-10">
            <a href="/country/czech" className="inline-flex items-center gap-2 text-sm font-semibold text-white/75 transition hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Чехийн үйлчилгээ рүү буцах
            </a>

            <div className="grid items-center gap-12 pb-12 pt-14 lg:grid-cols-[minmax(0,1.1fr)_360px] lg:pt-20">
              <div className="max-w-3xl">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45 }}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-200/30 bg-white/10 px-4 py-2 text-sm font-bold text-sky-50 backdrop-blur"
                >
                  <span className="text-base">🇨🇿</span> Чех Улсын ажлын байрны бүртгэл
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.52, delay: 0.08 }}
                  className="mt-5 max-w-3xl text-4xl font-black leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {CZECH_WORK_OPPORTUNITY.title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.16 }}
                  className="mt-5 max-w-2xl text-base leading-7 text-sky-50/85 sm:text-lg"
                >
                  Сонгон шалгаруулалттай ажлын байранд бүртгүүлж, Чех Улсад хууль ёсоор ажиллах болон амьдрах зорилгоо бодит төлөвлөгөөгөөр эхлүүлээрэй.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.23 }}
                  className="mt-7 flex flex-wrap gap-3"
                >
                  <a href="#register" className="inline-flex items-center gap-2 rounded-xl bg-[#ef3340] px-5 py-3.5 text-sm font-black text-white shadow-lg shadow-red-950/35 transition hover:-translate-y-0.5 hover:bg-[#d92734]">
                    Бүртгэлд хамрагдах <ArrowRight className="h-4 w-4" />
                  </a>
                  <a href={CZECH_WORK_CONTACT.phoneHref} className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-5 py-3.5 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20">
                    <Phone className="h-4 w-4" /> {CZECH_WORK_CONTACT.phoneDisplay} руу залгах
                  </a>
                </motion.div>
              </div>

              <motion.aside
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.2 }}
                className="overflow-hidden rounded-3xl border border-white/20 bg-white/95 p-6 shadow-2xl shadow-black/25 backdrop-blur"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-black tracking-[0.13em] text-[#ba1d2b]">БҮРТГЭЛИЙН ХУГАЦАА</p>
                  <CalendarDays className="h-5 w-5 text-[#ba1d2b]" />
                </div>
                <p className="mt-2 text-3xl font-black tracking-tight text-[#0b1f33]" style={{ fontFamily: "var(--font-display)" }}>{CZECH_WORK_OPPORTUNITY.registrationDeadline}</p>
                <div className="mt-5 grid grid-cols-2 gap-3 border-y border-slate-100 py-5">
                  <div>
                    <p className="text-2xl font-black text-[#0e5d92]">{CZECH_WORK_OPPORTUNITY.positions.total}</p>
                    <p className="mt-1 text-xs font-semibold leading-4 text-slate-500">нийт нээлттэй орон тоо</p>
                  </div>
                  <div>
                    <p className="text-sm font-black leading-5 text-[#0e5d92]">{CZECH_WORK_OPPORTUNITY.positions.women}</p>
                    <p className="mt-1 text-xs font-semibold leading-4 text-slate-500">{CZECH_WORK_OPPORTUNITY.positions.families}</p>
                  </div>
                </div>
                <div className="mt-5 flex items-start gap-3 rounded-2xl bg-sky-50 p-4">
                  <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-[#0e5d92]" />
                  <p className="text-sm font-bold leading-5 text-[#16405e]">{CZECH_WORK_OPPORTUNITY.departureTarget}</p>
                </div>
              </motion.aside>
            </div>
          </div>
        </section>

        <section className="container -mt-11 relative z-20">
          <div className="grid gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-950/5 md:grid-cols-4 md:p-6">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4 md:border-b-0 md:border-r md:pb-0">
              <div className="rounded-xl bg-sky-50 p-3 text-[#0e5d92]"><UsersRound className="h-5 w-5" /></div>
              <div><p className="text-xs font-bold text-slate-500">БҮРТГЭЛ</p><p className="mt-0.5 font-black text-[#0b1f33]">10 хүн · 6 эмэгтэй</p></div>
            </div>
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4 md:border-b-0 md:border-r md:pb-0">
              <div className="rounded-xl bg-emerald-50 p-3 text-emerald-700"><BriefcaseBusiness className="h-5 w-5" /></div>
              <div><p className="text-xs font-bold text-slate-500">АЖЛЫН ТӨРӨЛ</p><p className="mt-0.5 font-black text-[#0b1f33]">{CZECH_WORK_OPPORTUNITY.jobType}</p></div>
            </div>
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4 md:border-b-0 md:border-r md:pb-0">
              <div className="rounded-xl bg-red-50 p-3 text-[#ba1d2b]"><CreditCard className="h-5 w-5" /></div>
              <div><p className="text-xs font-bold text-slate-500">НИЙТ ТӨЛБӨР</p><p className="mt-0.5 font-black text-[#0b1f33]">€2,465 · 2 үе шаттай</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-emerald-50 p-3 text-emerald-700"><FileText className="h-5 w-5" /></div>
              <div><p className="text-xs font-bold text-slate-500">ЭХНИЙ МАТЕРИАЛ</p><p className="mt-0.5 font-black text-[#0b1f33]">Гадаад паспорт</p></div>
            </div>
          </div>
        </section>

        <section className="container grid gap-12 py-16 lg:grid-cols-[minmax(0,1fr)_390px] lg:py-20">
          <div className="space-y-14">
            <section>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-[#0e5d92] p-3 text-white"><BadgeCheck className="h-5 w-5" /></div>
                <div><p className="text-xs font-black tracking-[0.13em] text-[#0e5d92]">СОНГОН ШАЛГАРУУЛАЛТ</p><h2 className="mt-1 text-3xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Хэн бүртгүүлэх боломжтой вэ?</h2></div>
              </div>
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {CZECH_WORK_OPPORTUNITY.eligibility.map((item) => (
                  <div key={item} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#0e5d92]" /><p className="text-sm font-semibold leading-6 text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-3xl border border-sky-100 bg-gradient-to-br from-[#eaf5fc] via-white to-sky-50 p-6 sm:p-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                <div className="rounded-2xl bg-white p-4 text-[#0e5d92] shadow-sm"><FileText className="h-7 w-7" /></div>
                <div>
                  <p className="text-xs font-black tracking-[0.13em] text-[#0e5d92]">ЭХНИЙ БЭЛТГЭХ МАТЕРИАЛ</p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>{CZECH_WORK_OPPORTUNITY.firstMaterial}</h2>
                  <p className="mt-4 text-sm leading-7 text-slate-600">{CZECH_WORK_OPPORTUNITY.materialNote}</p>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-[#ba1d2b] p-3 text-white"><WalletCards className="h-5 w-5" /></div>
                <div><p className="text-xs font-black tracking-[0.13em] text-[#ba1d2b]">ЗАРДЛЫН ТӨЛӨВЛӨЛТ</p><h2 className="mt-1 text-3xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>2 үе шаттай төлбөр</h2></div>
              </div>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                {CZECH_WORK_OPPORTUNITY.payments.map((payment, index) => (
                  <article key={payment.amount} className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className={`absolute right-0 top-0 h-20 w-20 rounded-bl-full ${index === 0 ? "bg-sky-100" : "bg-red-100"}`} />
                    <p className="relative text-sm font-bold text-slate-500">{payment.label}</p>
                    <p className={`relative mt-3 text-4xl font-black ${index === 0 ? "text-[#0e5d92]" : "text-[#ba1d2b]"}`} style={{ fontFamily: "var(--font-display)" }}>{payment.amount}</p>
                    <p className="relative mt-3 text-sm font-semibold leading-6 text-slate-600">{payment.note}</p>
                  </article>
                ))}
              </div>
              <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">Төлбөрийн нөхцөл, гэрээний дэлгэрэнгүй болон тухайн ажлын байрны нөхцөлийг гэрээ байгуулахаас өмнө танилцуулж баталгаажуулна.</p>
            </section>

            <section>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-emerald-700 p-3 text-white"><Sparkles className="h-5 w-5" /></div>
                <div><p className="text-xs font-black tracking-[0.13em] text-emerald-700">ЧЕХ УЛСАД АЖИЛЛАХ БОЛОМЖ</p><h2 className="mt-1 text-3xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Танд нээгдэх давуу талууд</h2></div>
              </div>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                {CZECH_WORK_OPPORTUNITY.benefits.map((benefit, index) => {
                  const Icon = benefitIcons[index];
                  return <article key={benefit.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><Icon className="h-5 w-5" /></div><h3 className="mt-4 font-black text-[#0b1f33]" style={{ fontFamily: "var(--font-display)" }}>{benefit.title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{benefit.description}</p></article>;
                })}
              </div>
            </section>
          </div>

          <aside className="h-fit space-y-5 lg:sticky lg:top-24" id="register">
            <div className="rounded-3xl bg-[#0b2540] p-6 text-white shadow-xl shadow-[#0b2540]/20 sm:p-7">
              <p className="text-xs font-black tracking-[0.13em] text-sky-200">6–8 САРЫН БЭЛТГЭЛ</p>
              <h2 className="mt-2 text-2xl font-black leading-tight" style={{ fontFamily: "var(--font-display)" }}>Явах хүртэлх дараалал</h2>
              <div className="mt-6 space-y-5">
                {CZECH_WORK_OPPORTUNITY.timeline.map(([number, title, detail]) => (
                  <div key={number} className="flex gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-xs font-black text-sky-100">{number}</span><div><p className="font-bold text-white">{title}</p><p className="mt-1 text-xs leading-5 text-sky-100/80">{detail}</p></div></div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
              <p className="text-xs font-black tracking-[0.13em] text-[#ba1d2b]">БҮРТГЭЛИЙН ХОЛБОО</p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-[#0b1f33]" style={{ fontFamily: "var(--font-display)" }}>Одоо бүртгүүлээрэй</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">Орон тоо хязгаартай тул эхлээд утсаар холбогдож урьдчилсан шалгуураа нягтлуулна уу.</p>
              <a href={CZECH_WORK_CONTACT.phoneHref} className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-[#ef3340] px-4 py-3.5 text-sm font-black text-white transition hover:bg-[#d92734]"><Phone className="h-4 w-4" /> {CZECH_WORK_CONTACT.phoneDisplay}</a>
              <div className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm text-slate-600"><div className="flex gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#ba1d2b]" /><span>{CZECH_WORK_CONTACT.address}</span></div><div className="flex gap-3"><Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-[#ba1d2b]" /><span>{CZECH_WORK_CONTACT.officeHours}</span></div></div>
            </div>
          </aside>
        </section>

      </main>

      <Footer />
    </div>
  );
}
